// Copyright (c) Ping. All rights reserved.
//
// SINGLE SOURCE OF TRUTH for what is hidden from the conversation list.
//
// Every reason a row is excluded from the chat list lives here, each with a
// named reason, so the filtering is auditable in ONE place instead of being
// scattered across the read-model adapters. Both `conversationsAdapter.web.ts`
// and `conversationsAdapter.macos.ts` filter through `isConversationVisible`.
//
// NOTE ON THE TWO FILTER LAYERS:
//   1. LIST-MEMBERSHIP (this module) — decides whether a row is in the list at
//      all. Applied in the read-model adapter. Reasons below.
//   2. PANEL/TAB VIEW (ConversationListPanel) — decides which of the *visible*
//      rows show under the current tab. This is NOT exclusion from the list:
//        • archived rows (`archivedAt > 0`)  → shown under the Archive tab only
//        • the All / Unread / Favourites / DM / <folder> chips → transient view
//      Those rows are still "in the list"; they're just filtered by the active
//      tab. Only the reasons in THIS module remove a row from the list entirely.

import { isDeviceGroupConversationId } from './deviceGroup';
import { isRegisteredCallGroup } from '../calls/callGroupRegistry';
import { CALL_GROUP_NAME_PREFIX } from '../conversation/groupConversation';

/** Why a conversation is excluded from the chat list. */
export type ConversationHiddenReason =
  /** The user deleted it (soft-delete marker `deletedAt > 0`). History is
   *  cleared but the row is kept hidden so a future message can resume it. */
  | 'soft-deleted'
  /** The SERVER authoritatively 404s this MLS group (`orphanedAt > 0`) — a
   *  leaked call group, a peer-deleted group, or a dead invite. Hidden but
   *  recoverable on a genuine revive. */
  | 'orphaned'
  /** The SDK's local-only personal DEVICE GROUP (deterministic `ffdc…` id) —
   *  cross-device state, never a real chat. */
  | 'device-group'
  /** An ephemeral per-CALL MLS group (`call:<id>` name / registered call id) —
   *  call infrastructure, driven by the calls subsystem, not a chat. */
  | 'call-group';

/** Minimal structural view of a row needed to decide list visibility. */
export interface ConversationVisibilityInput {
  readonly id: string;
  readonly name?: string | undefined;
  readonly deletedAt?: number | undefined;
  /** Wall-clock ms the server orphaned this MLS group; > 0 → excluded. */
  readonly orphanedAt?: number | undefined;
  /** Wall-clock ms the local user left this group (or the server removed them).
   *  A left group is NO LONGER hidden — it stays in the list as a READ-ONLY
   *  row so a re-invite resumes it with its full pre-exit history intact. The
   *  composer goes read-only off this marker (`hasLeftGroup`) and `persistIfNew`
   *  clears it on the re-invite Welcome. To actually remove a left chat the user
   *  must explicitly Delete it (`deletedAt`). */
  readonly leftAt?: number | undefined;
}

/**
 * The reason a conversation is hidden from the list, or `null` when it is
 * visible. This is the ONE function that decides list membership — add or audit
 * exclusion rules here and nowhere else.
 *
 * Deliberately does NOT hide by empty `name`: a real chat whose title hasn't
 * hydrated yet must still surface (its title is derived by
 * `deriveConversationTitle`). Hiding blank-name rows previously dropped joined
 * groups whose meta hadn't loaded ("missing chats").
 */
export function conversationHiddenReason(
  row: ConversationVisibilityInput,
): ConversationHiddenReason | null {
  if ((row.deletedAt ?? 0) > 0) return 'soft-deleted';
  if ((row.orphanedAt ?? 0) > 0) return 'orphaned';
  // A LEFT group (`leftAt > 0`) is intentionally NOT hidden — it stays in the
  // list as a read-only row so a re-invite can resume it with full history.
  // See the `leftAt` field doc above. (Removal is an explicit Delete →
  // `deletedAt`, which the `soft-deleted` branch handles.)
  if (isDeviceGroupConversationId(row.id)) return 'device-group';
  if (
    isRegisteredCallGroup(row.id) ||
    (row.name ?? '').startsWith(CALL_GROUP_NAME_PREFIX)
  ) {
    return 'call-group';
  }
  return null;
}

/** True when a conversation should appear in the chat list. */
export function isConversationVisible(row: ConversationVisibilityInput): boolean {
  return conversationHiddenReason(row) === null;
}

/**
 * Partition a set of rows into visible + hidden (with reasons). Handy for
 * diagnostics — e.g. a dev-console dump of exactly what's filtered out and why.
 */
export function partitionConversationVisibility<T extends ConversationVisibilityInput>(
  rows: readonly T[],
): { visible: T[]; hidden: Array<{ row: T; reason: ConversationHiddenReason }> } {
  const visible: T[] = [];
  const hidden: Array<{ row: T; reason: ConversationHiddenReason }> = [];
  for (const row of rows) {
    const reason = conversationHiddenReason(row);
    if (reason === null) visible.push(row);
    else hidden.push({ row, reason });
  }
  return { visible, hidden };
}
